import { useCallback, useEffect, useState } from 'react';
import OgShell from '../components/OgShell';
import AuthGate from '../components/AuthGate';
import { inviteQiPractitioner, listQiUsers, setPractitionerDirectory, setPractitionerStatus } from '../lib/api';
import type { QiUserRow } from '../lib/api';
import { formatDate, PRACTITIONER_STATUS_LABELS } from '../lib/constants';

const ROLE_LABELS: Record<string, string> = {
  admin: '系統管理員',
  reviewer: '見證覆核者',
};

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'none', label: '一般會員｜取消施炁者資格' },
  { value: 'candidate', label: '準施炁者｜實作培育中' },
  { value: 'mentor_review', label: '準施炁者｜導師覆核中' },
  { value: 'authorized', label: '正式授權施炁者' },
  { value: 'suspended', label: '已停權' },
];

const STATUS_BADGE_CLASS: Record<string, string> = {
  none: 'bg-background-200 text-foreground-600',
  candidate: 'bg-accent-100 text-accent-800',
  mentor_review: 'bg-secondary-100 text-secondary-900',
  authorized: 'bg-emerald-100 text-emerald-700',
  suspended: 'bg-foreground-200 text-foreground-600',
};

export default function AdminRolesPage() {
  return (
    <OgShell>
      <div className="max-w-3xl mx-auto px-4 md:px-6">
        <AuthGate requireStaff>
          {(auth) => (auth.role === 'admin' ? <RoleManager /> : <NotAdmin />)}
        </AuthGate>
      </div>
    </OgShell>
  );
}

function RoleManager() {
  const [users, setUsers] = useState<QiUserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteStatus, setInviteStatus] = useState<'candidate' | 'authorized'>('candidate');
  const [inviting, setInviting] = useState(false);
  const [directoryDrafts, setDirectoryDrafts] = useState<Record<string, {
    publicDisplayName: string;
    locations: string[];
    visible: boolean;
  }>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setUsers(await listQiUsers());
    } catch (err) {
      setError(err instanceof Error ? err.message : '載入失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSetStatus = useCallback(
    async (u: QiUserRow, status: string) => {
      if (!status) return;
      setBusyId(u.user_id);
      setActionMsg(null);
      try {
        await setPractitionerStatus(u.user_id, status);
        const label = PRACTITIONER_STATUS_LABELS[status] || status;
        setActionMsg(`已將 ${u.email || '該使用者'} 設定為「${label}」。`);
        await load();
      } catch (err) {
        setActionMsg(err instanceof Error ? err.message : '設定失敗');
      } finally {
        setBusyId(null);
      }
    },
    [load],
  );

  const handleSaveDirectory = useCallback(async (u: QiUserRow) => {
    const draft = directoryDrafts[u.user_id] || { publicDisplayName: '', locations: [], visible: false };
    if (draft.visible && !draft.publicDisplayName.trim()) {
      setActionMsg('顯示於掛號名單時，必須填寫半匿名名稱，例如「詹老師」。');
      return;
    }
    setBusyId(u.user_id);
    setActionMsg(null);
    try {
      await setPractitionerDirectory({
        userId: u.user_id,
        publicDisplayName: draft.publicDisplayName,
        practiceLocations: draft.locations,
        directoryVisible: draft.visible,
      });
      setActionMsg(`已更新 ${u.email || '該施炁者'} 的掛號顯示資料。`);
    } catch (err) {
      setActionMsg(err instanceof Error ? err.message : '掛號資料設定失敗');
    } finally {
      setBusyId(null);
    }
  }, [directoryDrafts]);

  const handleInvite = useCallback(async () => {
    const email = inviteEmail.trim().toLowerCase();
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setActionMsg('請輸入正確的 Email。');
      return;
    }
    setInviting(true);
    setActionMsg(null);
    try {
      await inviteQiPractitioner(email, inviteStatus);
      const label = inviteStatus === 'authorized' ? '正式授權施炁者' : '準施炁者';
      setActionMsg(`已邀請 ${email} 並設定為「${label}」；本人完成信箱驗證前無法登入。`);
      setInviteEmail('');
      await load();
    } catch (err) {
      setActionMsg(err instanceof Error ? err.message : '邀請失敗');
    } finally {
      setInviting(false);
    }
  }, [inviteEmail, inviteStatus, load]);

  if (loading) {
    return (
      <div className="py-20 text-center">
        <div className="w-10 h-10 mx-auto border-2 border-accent-300 border-t-accent-500 rounded-full animate-spin" />
        <p className="text-sm text-foreground-500 mt-3">載入中……</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-16 text-center space-y-3">
        <p className="text-sm text-rose-600">{error}</p>
        <button
          onClick={load}
          className="whitespace-nowrap px-4 py-2 rounded-md border border-foreground-200 text-sm cursor-pointer"
        >
          重試
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="space-y-1 pt-2">
        <h1 className="text-xl md:text-2xl text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>
          施炁者培育狀態管理
        </h1>
        <p className="text-sm text-foreground-500">
          設定會員的施炁者培育狀態。僅系統管理員可操作；正式授權仍須由 ABAA／管理員人工核定，不會自動升級。
        </p>
        <p className="text-xs text-foreground-400">
          管理權（ADM）與施炁專業資格是兩套獨立授權：此頁只管理「施炁專業資格」，ADM 授權請至 ADM Center（/adm）。使用者即使是 ADM，也需另行授予 candidate／authorized 專業資格，不會因 ADM 身分自動成為施炁者。
        </p>
      </header>

      <section className="bg-background-100 border border-foreground-200/50 rounded-xl p-4 space-y-3">
        <div>
          <h2 className="text-base text-foreground-800">邀請新的施炁者</h2>
          <p className="text-xs text-foreground-500 mt-1">
            輸入 Email 後立即指派資格並寄出驗證信；本人完成信箱驗證前會顯示「已邀請、尚未啟用」，且不能登入。
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input
            type="email"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            placeholder="new-member@example.com"
            className="min-w-[240px] flex-1 px-3 py-2 rounded-md border border-foreground-200 bg-background-50 text-sm focus:outline-none focus:border-accent-300"
          />
          <select
            value={inviteStatus}
            onChange={(e) => setInviteStatus(e.target.value as 'candidate' | 'authorized')}
            className="px-3 py-2 rounded-md border border-foreground-200 bg-background-50 text-sm cursor-pointer"
          >
            <option value="candidate">準施炁者</option>
            <option value="authorized">正式授權施炁者</option>
          </select>
          <button
            type="button"
            disabled={inviting || !inviteEmail.trim()}
            onClick={handleInvite}
            className="whitespace-nowrap px-4 py-2 rounded-md bg-accent-600 text-white text-sm cursor-pointer disabled:opacity-50"
          >
            {inviting ? '邀請中……' : '指派資格並寄出邀請'}
          </button>
        </div>
      </section>

      {actionMsg && <p className="text-xs text-foreground-500">{actionMsg}</p>}

      <div className="space-y-2">
        {users.length === 0 ? (
          <p className="text-sm text-foreground-400 bg-background-100 border border-foreground-200/50 rounded-xl p-6 text-center">
            目前沒有已註冊的使用者。
          </p>
        ) : (
          users.map((u) => {
            const roleLabel = u.role ? ROLE_LABELS[u.role] || u.role : '一般成員';
            const status = u.practitioner_status;
            return (
              <div
                key={u.user_id}
                className="bg-background-100 border border-foreground-200/50 rounded-xl p-4 flex items-center justify-between gap-3 flex-wrap"
              >
                <div className="space-y-1 min-w-[180px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium text-foreground-800">{u.email || '（無信箱）'}</p>
                    {u.legal_name && <span className="text-xs text-foreground-500">實名：{u.legal_name}</span>}
                  </div>
                  <p className="text-xs text-foreground-400">
                    註冊於 {formatDate(u.created_at)} &middot; {roleLabel}
                  </p>
                  <span className={`inline-flex text-xs px-2 py-0.5 rounded-full ${
                    u.email_confirmed_at
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-amber-100 text-amber-700'
                  }`}>
                    {u.email_confirmed_at ? '已啟用' : '已邀請、尚未啟用'}
                  </span>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className={`text-xs px-2.5 py-1 rounded-full whitespace-nowrap ${
                      status && STATUS_BADGE_CLASS[status] ? STATUS_BADGE_CLASS[status] : 'bg-background-200 text-foreground-500'
                    }`}
                  >
                    {status ? PRACTITIONER_STATUS_LABELS[status] || status : '未設定培育狀態'}
                  </span>

                  <select
                    value={status || ''}
                    disabled={busyId === u.user_id}
                    onChange={(e) => handleSetStatus(u, e.target.value)}
                    className="px-2.5 py-1.5 rounded-md border border-foreground-200 bg-background-50 text-xs text-foreground-700 focus:outline-none focus:border-accent-300 cursor-pointer disabled:opacity-50"
                  >
                    <option value="" disabled>
                      設定培育狀態…
                    </option>
                    {STATUS_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                {status === 'authorized' && (
                  <div className="w-full mt-3 border-t border-foreground-200/60 pt-3 space-y-3">
                    <p className="text-xs font-medium text-foreground-700">掛號名單設定</p>
                    <input
                      type="text"
                      value={directoryDrafts[u.user_id]?.publicDisplayName || ''}
                      onChange={(event) => setDirectoryDrafts((old) => ({
                        ...old,
                        [u.user_id]: {
                          ...(old[u.user_id] || { locations: [], visible: false }),
                          publicDisplayName: event.target.value,
                        },
                      }))}
                      placeholder="半匿名名稱，例如：詹老師"
                      className="w-full px-3 py-2 rounded-md border border-foreground-200 bg-background-50 text-xs"
                    />
                    <div className="flex items-center gap-4 flex-wrap text-xs">
                      {[
                        { value: 'taipei', label: '臺北' },
                        { value: 'hualien', label: '花蓮' },
                      ].map((location) => (
                        <label key={location.value} className="flex items-center gap-1.5">
                          <input
                            type="checkbox"
                            checked={(directoryDrafts[u.user_id]?.locations || []).includes(location.value)}
                            onChange={(event) => setDirectoryDrafts((old) => {
                              const current = old[u.user_id] || { publicDisplayName: '', locations: [], visible: false };
                              const locations = event.target.checked
                                ? [...new Set([...current.locations, location.value])]
                                : current.locations.filter((value) => value !== location.value);
                              return { ...old, [u.user_id]: { ...current, locations } };
                            })}
                          />
                          {location.label}
                        </label>
                      ))}
                      <label className="flex items-center gap-1.5">
                        <input
                          type="checkbox"
                          checked={directoryDrafts[u.user_id]?.visible || false}
                          onChange={(event) => setDirectoryDrafts((old) => ({
                            ...old,
                            [u.user_id]: {
                              ...(old[u.user_id] || { publicDisplayName: '', locations: [] }),
                              visible: event.target.checked,
                            },
                          }))}
                        />
                        顯示於案主掛號名單
                      </label>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleSaveDirectory(u)}
                      disabled={busyId === u.user_id}
                      className="px-3 py-1.5 rounded-md bg-emerald-700 text-white text-xs disabled:opacity-50"
                    >
                      儲存掛號顯示資料
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      <div className="bg-foreground-50/60 rounded-xl p-4 text-xs text-foreground-500 leading-relaxed space-y-1">
        <p>· <strong>一般會員（none）</strong>：取消準施炁者／施炁者資格，但不會停權會員帳號。</p>
        <p>· <strong>準施炁者（candidate）</strong>：可建立私人案例、填寫施炁紀錄與追蹤，但不得公開見證或自稱正式授權。</p>
        <p>· <strong>導師覆核（mentor_review）</strong>：培育進入導師覆核階段，權限等同 candidate。</p>
        <p>· <strong>正式授權（authorized）</strong>：完整施炁者權限，可建立並經覆核後公開見證。</p>
        <p>· <strong>已停權（suspended）</strong>：無法建立新案例。</p>
      </div>
    </div>
  );
}

function NotAdmin() {
  return (
    <div className="py-16 text-center space-y-4">
      <div className="w-16 h-16 mx-auto rounded-full bg-foreground-100 flex items-center justify-center">
        <i className="ri-admin-line text-foreground-400 text-2xl" />
      </div>
      <h2 className="text-lg text-foreground-800" style={{ fontFamily: 'var(--font-heading)' }}>
        僅系統管理員可管理培育狀態
      </h2>
      <p className="text-sm text-foreground-500 max-w-sm mx-auto">培育狀態管理僅開放給系統管理員（ABAA）。</p>
    </div>
  );
}
